/**
 * Local draft of a split test, shared by the new-test wizard (page tests) and
 * the editor's Launch tab (element tests). Both read and write the same
 * `humblytics_draft_split_test_<id>` entry so a draft resumes where it left off.
 */
import type { AudienceRestriction, GeoRestriction } from "@/lib/types/experiment-restrictions"

export const DRAFT_STORAGE_KEY = "humblytics_draft_split_test_"
const INSTRUCTIONS_KEY = "humblytics_draft_instructions_"

export interface DraftVariantInput {
  id: string
  url: string
  label: string
  notes: string
  isControl: boolean
  screenshots?: string[]
}

export interface DraftSplitTestState {
  draftId: string
  createdAt: string
  updatedAt: string
  propertyId: string
  experimentName: string
  testGoal: string
  finishPageUrl: string
  isUserLevel: boolean
  testType: "page" | "element"
  experimentNotes: string
  trafficDistribution: number[]
  geoRestriction: GeoRestriction
  audienceRestriction: AudienceRestriction
  variants: DraftVariantInput[]
  componentPageUrl: string
  currentStep: number
  selectedPropertyForTest?: string // For workspace aggregate mode
  autoStopDurationDays: number | null
}

/** UUID v4 for a new draft. */
export function generateDraftId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID()
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === "x" ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export function readSplitTestDraft(draftId: string): DraftSplitTestState | null {
  try {
    const raw = localStorage.getItem(`${DRAFT_STORAGE_KEY}${draftId}`)
    if (!raw) return null
    const draft = JSON.parse(raw)
    return draft?.draftId ? (draft as DraftSplitTestState) : null
  } catch {
    return null
  }
}

export function writeSplitTestDraft(draft: DraftSplitTestState): boolean {
  try {
    localStorage.setItem(
      `${DRAFT_STORAGE_KEY}${draft.draftId}`,
      JSON.stringify({ ...draft, updatedAt: new Date().toISOString() }),
    )
    return true
  } catch {
    return false
  }
}

/** Drops the draft and the editor's saved changes once the test is live. */
export function clearSplitTestDraft(draftId: string) {
  try {
    localStorage.removeItem(`${DRAFT_STORAGE_KEY}${draftId}`)
    localStorage.removeItem(`${INSTRUCTIONS_KEY}${draftId}`)
  } catch {
    // Storage unavailable: nothing to clean up.
  }
}

/** Saved editor changes for one version, from the editor's local copy. */
export function readDraftVariantInstructions(draftId: string, variantId: string): unknown[] {
  try {
    const raw = localStorage.getItem(`${INSTRUCTIONS_KEY}${draftId}`)
    if (!raw) return []
    const list = JSON.parse(raw)?.variants?.[variantId]?.instructions
    return Array.isArray(list) ? list : []
  } catch {
    return []
  }
}

export function getAllDraftSplitTests(): DraftSplitTestState[] {
  const drafts: DraftSplitTestState[] = []
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key?.startsWith(DRAFT_STORAGE_KEY)) continue
      try {
        const draft = JSON.parse(localStorage.getItem(key) || "")
        if (draft?.draftId) drafts.push(draft)
      } catch {
        // Ignore invalid entries
      }
    }
  } catch {
    return []
  }
  return drafts.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
}

/**
 * A fresh element-test draft for a picked page: Control and B on the same
 * URL, even split, no audience limits, stopped by hand.
 */
export function newElementDraft(input: {
  draftId: string
  propertyId: string
  pagePath: string
  testGoal: string
  experimentName: string
  autoStopDurationDays?: number | null
  selectedPropertyForTest?: string
}): DraftSplitTestState {
  const now = new Date().toISOString()
  return {
    draftId: input.draftId,
    createdAt: now,
    updatedAt: now,
    propertyId: input.propertyId,
    experimentName: input.experimentName,
    testGoal: input.testGoal,
    finishPageUrl: "",
    isUserLevel: false,
    testType: "element",
    experimentNotes: "",
    trafficDistribution: [50, 50],
    geoRestriction: { enabled: false, countries: [] },
    audienceRestriction: { enabled: false, devices: [], channels: [] },
    variants: [
      { id: "A", url: input.pagePath, label: "Control", notes: "", isControl: true },
      { id: "B", url: input.pagePath, label: "Variant B", notes: "", isControl: false },
    ],
    componentPageUrl: input.pagePath,
    // 1 = past setup: the wizard resumes a page test on Variants, the editor ignores it.
    currentStep: 1,
    selectedPropertyForTest: input.selectedPropertyForTest,
    autoStopDurationDays: input.autoStopDurationDays ?? null,
  }
}
